import { useCallback, useEffect, useState } from "react";
import { useAuth0 } from "@auth0/auth0-react";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  Paper,
  Snackbar,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import { DataGridPro, GridColDef, GridActionsCellItem } from "@mui/x-data-grid-pro";
import { ConfirmationDialog } from "../../pricing-management/pages/deals-page/delete-dilog.tsx";
import AdminAPI, { AdminUser, Organization, Team } from "../api/admin-api.ts";

const USER_PANEL_COLUMNS: GridColDef[] = [
  {
    field: "name",
    headerName: "Name",
    flex: 1,
    minWidth: 150,
    valueGetter: (_value, row) => `${row.first_name ?? ""} ${row.last_name ?? ""}`.trim(),
  },
  { field: "email", headerName: "Email", flex: 1, minWidth: 200 },
  {
    field: "is_admin",
    headerName: "Admin",
    width: 80,
    valueFormatter: (value) => (value ? "Yes" : "No"),
  },
  {
    field: "blocked",
    headerName: "Blocked",
    width: 90,
    valueFormatter: (value) => (value ? "Yes" : "No"),
  },
];

function TeamUsersPanel({ users }: { users: AdminUser[] }) {
  if (users.length === 0) {
    return <Typography variant="body2" sx={{ p: 2 }}>No users in this team.</Typography>;
  }
  return (
    <Box sx={{ p: 2, backgroundColor: "#fafafa" }}>
      <DataGridPro
        rows={users}
        columns={USER_PANEL_COLUMNS}
        density="compact"
        hideFooter
        sx={{ border: 0, backgroundColor: "white" }}
      />
    </Box>
  );
}

function TeamsPage() {
  const { getAccessTokenSilently } = useAuth0();
  const [teams, setTeams] = useState<Team[]>([]);
  const [organizations, setOrganizations] = useState<Organization[]>([]);
  const [users, setUsers] = useState<AdminUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [snackMessage, setSnackMessage] = useState<string | undefined>(undefined);

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState<Team | null>(null);
  const [name, setName] = useState("");
  const [organizationId, setOrganizationId] = useState("");

  const [deleteTarget, setDeleteTarget] = useState<Team | null>(null);

  const organizationNames = new Map(organizations.map((organization) => [organization.id, organization.name]));

  const fetchData = useCallback(async () => {
    setLoading(true);
    const token = await getAccessTokenSilently();
    const [teamsResult, organizationsResult, usersResult] = await Promise.all([
      AdminAPI.getTeams(token),
      AdminAPI.getOrganizations(token),
      AdminAPI.getUsers(token),
    ]);
    setLoading(false);
    if (!teamsResult.valid) return setSnackMessage(teamsResult.error);
    if (!organizationsResult.valid) return setSnackMessage(organizationsResult.error);
    if (!usersResult.valid) return setSnackMessage(usersResult.error);
    setTeams(teamsResult.body);
    setOrganizations(organizationsResult.body);
    setUsers(usersResult.body);
  }, [getAccessTokenSilently]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const openCreateDialog = () => {
    setEditing(null);
    setName("");
    setOrganizationId("");
    setDialogOpen(true);
  };

  const openEditDialog = (team: Team) => {
    setEditing(team);
    setName(team.name);
    setOrganizationId(team.organization_id);
    setDialogOpen(true);
  };

  const canSave = name.trim().length > 0 && organizationId !== "";

  const handleSave = async () => {
    if (!canSave) return;
    const payload = { name: name.trim(), organization_id: organizationId };
    const token = await getAccessTokenSilently();
    const result = editing
      ? await AdminAPI.updateTeam(token, editing.id, payload)
      : await AdminAPI.createTeam(token, payload);
    if (!result.valid) return setSnackMessage(result.error);
    setDialogOpen(false);
    setSnackMessage(editing ? "Team updated." : "Team created.");
    fetchData();
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    const token = await getAccessTokenSilently();
    const result = await AdminAPI.deleteTeam(token, deleteTarget.id);
    setDeleteTarget(null);
    if (!result.valid) return setSnackMessage(result.error);
    setSnackMessage("Team deleted.");
    fetchData();
  };

  const columns: GridColDef[] = [
    { field: "name", headerName: "Name", flex: 1, minWidth: 150 },
    {
      field: "organization_id",
      headerName: "Organization",
      flex: 1,
      minWidth: 150,
      valueFormatter: (value) => organizationNames.get(value as string) ?? "N/A",
    },
    {
      field: "actions",
      type: "actions",
      headerName: "Actions",
      width: 100,
      getActions: (params) => [
        <GridActionsCellItem
          icon={<EditIcon fontSize="small" />}
          label="Edit"
          onClick={() => openEditDialog(params.row as Team)}
        />,
        <GridActionsCellItem
          icon={<DeleteIcon fontSize="small" />}
          label="Delete"
          onClick={() => setDeleteTarget(params.row as Team)}
        />,
      ],
    },
  ];

  return (
    <Stack gap={2} sx={{ flex: 1, display: "flex", flexDirection: "column" }}>
      <Stack direction="row" justifyContent="flex-end">
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={openCreateDialog}
          sx={{ color: "white" }}
        >
          Add Team
        </Button>
      </Stack>

      <Paper sx={{ flex: 1, display: "flex", flexDirection: "column", minHeight: 0 }}>
        <DataGridPro
          rows={teams}
          columns={columns}
          filterMode="client"
          disableMultipleColumnsSorting={false}
          disableMultipleColumnsFiltering={false}
          sx={{ border: 0 }}
          loading={loading}
          getDetailPanelContent={({ row }) => (
            <TeamUsersPanel users={users.filter((user) => user.team_id === row.id)} />
          )}
          getDetailPanelHeight={() => "auto"}
        />
      </Paper>

      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)}>
        <DialogTitle>{editing ? "Edit Team" : "Add Team"}</DialogTitle>
        <DialogContent sx={{ minWidth: "400px" }}>
          <Stack gap={2} sx={{ mt: 1 }}>
            <TextField
              label="Name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              size="small"
              required
              fullWidth
            />
            <TextField
              label="Organization"
              value={organizationId}
              onChange={(e) => setOrganizationId(e.target.value)}
              size="small"
              select
              required
              fullWidth
            >
              {organizations.map((organization) => (
                <MenuItem key={organization.id} value={organization.id}>{organization.name}</MenuItem>
              ))}
            </TextField>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)} variant="outlined">Cancel</Button>
          <Button onClick={handleSave} variant="contained" sx={{ color: "white" }} disabled={!canSave}>Save</Button>
        </DialogActions>
      </Dialog>

      <ConfirmationDialog
        title="Delete Team"
        description={`Are you sure you want to delete "${deleteTarget?.name}"? This action cannot be undone.`}
        open={deleteTarget !== null}
        onClose={() => setDeleteTarget(null)}
        onAccept={handleDelete}
      />

      <Snackbar
        open={snackMessage !== undefined}
        autoHideDuration={6000}
        onClose={() => setSnackMessage(undefined)}
        message={snackMessage}
      />
    </Stack>
  );
}

export default TeamsPage;
